import '@/src/imports'
import { Stack, useSegments, router, usePathname, ErrorBoundaryProps } from 'expo-router'
import { ApolloProvider } from '@apollo/client'
import { mesaColorNames, typography } from '@/src/common/theme'
import { apolloClient } from '@/src/common/gql/apolloClient'
import { isRootPath, plaidLinks, useAppsFlyer } from '@/src/services/AppsFlyer'
import { useGetUserInformation } from '@/src/common/hooks/useGetUserInformation'
import { useIsUserSignedIn } from '@/src/common/hooks/useIsUserSignedIn'
import { StatsigProvider, useStatsig, useStatsigUser } from '@/src/services/Statsig'
import * as Sentry from '@sentry/react-native'
import { useStatusBarColor } from '@/src/common/hooks/useStatusBarColor'
import { PortalProvider, TamaguiProvider } from 'tamagui'
import { AlertProvider } from '@/src/components/ui/elements/dialogs/Alert'
import { SardineProvider } from '@/src/services/Sardine'
import { Tamagui } from '@/tamagui.config'
import { Experiences } from '@/src/experiences'
import { MesaToast } from '@/src/common/components/MesaToast'
import { MesaSentry } from '@/src/services/Sentry'
import { useRequestAppPermissions } from '../common/hooks/useRequestAppPermissions'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import { NewReferralNotification } from '../common/components/Referrals/NewReferralNotification'
import { useAppState } from '@/src/common/hooks/useAppState'
import { type ComponentProps, useEffect, useState } from 'react'
import AsyncStorage, { useAsyncStorage } from '@react-native-async-storage/async-storage'
import {
  useTokenStore,
  tokenStoreLoadingPromise,
  useTokenStoreLoaded,
} from '@/src/common/store/useTokenStore'
import * as segmentClient from '@/src/services/segment'
import { getInitialURL, parseInitialURLAsync } from 'expo-linking'
import { sentryCaptureException } from '@/src/common/hooks/useLogError'
import { Routes } from '@/src/common/enums/routes'
import { isEqual } from 'lodash'
import {
  Platform,
  TouchableOpacity,
  useWindowDimensions,
  Text,
  View,
  ScrollView,
  Image,
} from 'react-native'
import { z } from 'zod/v4'
import { SafeAreaView } from 'react-native-safe-area-context'
import * as Updates from 'expo-updates'
import { version } from '@/package.json'
import { isProduction } from '@/src/common/environment'
import { FontLoadingGate } from '@/src/common/theme/fonts/FontLoadingGate'
import type { StatsigUser } from '@statsig/client-core'
import { useTrackPage } from '@/src/common/hooks/useTrackPage'
import { Provider as ReduxProvider } from 'react-redux'
import { store } from '@/src/store'
import { AuthenticationApi } from '@/src/experiences/authentication/store/api'

// eslint-disable-next-line @typescript-eslint/naming-convention
export const unstable_settings = {
  // Ensure any route can link back to `/`
  initialRouteName: 'index',
}

const PartnerNameCode = z.string({ message: 'Partner name code is not a string' })

if (Platform.OS === 'web') {
  void getInitialURL().then(async (url) => {
    if (url) {
      try {
        const parsedUrl = await parseInitialURLAsync()

        // If partner_name is present, we're going to persist it to be passed on during Account Creation
        // We purposefully do not clear this value in case the user comes back at another point in time
        // to complete the account creation process
        if (parsedUrl.queryParams?.partner_name) {
          const { data: partnerNameCode } = PartnerNameCode.safeParse(
            parsedUrl.queryParams.partner_name,
          )
          if (partnerNameCode) {
            await AsyncStorage.setItem('partner_name', partnerNameCode)
          }
        }

        if (parsedUrl.path && !isRootPath(parsedUrl.path)) {
          const urlObject = new URL(url)
          void AsyncStorage.setItem(
            'deferred_navigation_url',
            `${urlObject.pathname}${urlObject.search}${urlObject.hash}`,
          )
        }
      } catch (error) {
        sentryCaptureException('Error setting deferred_navigation_url from initial url', error)
      }
    }
  })
}

function useGlobalImperativeRedirects() {
  const [loadingTokens, setLoadingTokens] = useState(() => !useTokenStoreLoaded.getState().loaded)
  const { hasTokens } = useTokenStore()
  const { loading: tokenStoreLoading, isSignedInWithPhoneNumber } = useIsUserSignedIn()

  const { state: currentAppState } = useAppState()

  const appStateIsResumed = 'active' === currentAppState

  const { getItem: getDeferredNavigationUrl, removeItem: deleteDeferredNavigationUrl } =
    useAsyncStorage('deferred_navigation_url')

  const { initialized: userInformationInitialized, user: userInformation } = useGetUserInformation()
  const isSignedIn = !loadingTokens && hasTokens()
  const segment = useSegments()
  const pathname = usePathname()

  const getNotificationPermissions = Experiences.Notifications.Hooks.useGetNotificationPermissions()

  const isGettingStarted = Routes.Root.route === pathname
  const isCurrentlyOnboarding = segment[0] === 'onboarding'

  const accountCreationSegment = ['onboarding', 'AccountCreation'] satisfies typeof segment

  const isNotFound = segment[0] === '+not-found'

  const accountCreationVerifyPhoneOtpSegment = [
    'onboarding',
    'AccountCreationVerifyPhoneOtp',
  ] satisfies typeof segment

  const isAccountCreationSegment = isEqual(accountCreationSegment, segment)
  const isAccountCreationVerifyPhoneOtpSegment = isEqual(
    accountCreationVerifyPhoneOtpSegment,
    segment,
  )

  const isAccountCreation = isAccountCreationSegment || isAccountCreationVerifyPhoneOtpSegment

  useEffect(() => {
    const setLoadingOnceTokenStoreIsFinishedLoading = async () => {
      await tokenStoreLoadingPromise
      setLoadingTokens(false)
    }

    void setLoadingOnceTokenStoreIsFinishedLoading()
  }, [])

  useEffect(() => {
    if (currentAppState === 'inactive') void segmentClient.flush()
  }, [currentAppState])

  useEffect(() => {
    if (
      ['ios', 'android'].includes(Platform.OS) &&
      plaidLinks.some((plaidLink) => plaidLink === pathname)
    ) {
      router.canGoBack() &&
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        router.back()
    }
  }, [pathname])

  useEffect(
    () => {
      async function navigateToAuthedStack() {
        if (segment[0] !== 'main') {
          // DO NOT CHANGE THE FOLLOWING LINE TO USE `setTimeout` (even if it fixes the "navigation before root navigation mounted error"). some screens, such as the `RequestNotifications`, depend on this global async imperative route handler
          router.replace(Routes.Main.route)
        }

        const deferred_navigation_url = await getDeferredNavigationUrl()
        try {
          // @ts-expect-error too lazy to cast to expo router type
          deferred_navigation_url && router.navigate(deferred_navigation_url, { withAnchor: true })

          if (
            getNotificationPermissions &&
            !getNotificationPermissions.granted &&
            getNotificationPermissions.canAskAgain &&
            getNotificationPermissions.isMoreThan1MonthSinceLastRequest &&
            ['ios', 'android'].includes(Platform.OS)
          ) {
            router.navigate(Experiences.Notifications.Navigation.Routes.Request.route)
          }
        } catch (error) {
          sentryCaptureException(
            'Error handling deferred_navigation_url',
            error,
            deferred_navigation_url,
          )
        } finally {
          await deleteDeferredNavigationUrl()
        }
      }

      if (!tokenStoreLoading && isSignedInWithPhoneNumber) {
        if (userInformation?.phoneNumber && userInformation.firstName && userInformation.lastName) {
          void navigateToAuthedStack()
        }
        // we don't have account creation anymore, so if the user is in an auth state where we go into account creation
        // https://mesamember.slack.com/archives/C07PJ1YEKSS/p1758559873583039
        // else if (userInformationInitialized && !isAccountCreation) {
        //   router.push(Routes.AccountCreation.route)
        // }
      } else {
        if (!isCurrentlyOnboarding && !isGettingStarted) {
          // router.replace(Routes.Root.route)
          // King: Bandaid, need to update Router Implementation to wait until
          // Nav mounted
          setTimeout(() => router.replace(Routes.Root.route))
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isSignedIn,
      isSignedInWithPhoneNumber,
      tokenStoreLoading,
      appStateIsResumed,
      userInformation,
      userInformationInitialized,
      loadingTokens,
      isAccountCreation,
      isCurrentlyOnboarding,
      isGettingStarted,
      isNotFound,
    ],
  )
}

const GlobalNavigator = () => (
  <Stack screenOptions={{ headerShown: false }}>
    <Stack.Screen name="index" />
    <Stack.Screen name="+not-found" />

    <Stack.Screen name="main" />
    <Stack.Screen name="onboarding" />
    <Stack.Screen name="get-started" />

    <Stack.Screen
      name="sandbox-plaid-oauth-reply"
      options={{ presentation: 'modal', headerShown: false, gestureEnabled: false }}
    />
    <Stack.Screen
      name="plaid-oauth-reply"
      options={{ presentation: 'modal', headerShown: false, gestureEnabled: false }}
    />
  </Stack>
)

function RootLayoutNav({ statsigUser }: { statsigUser: StatsigUser }) {
  useStatusBarColor()
  useGetUserInformation()
  useAppsFlyer()
  useRequestAppPermissions()
  useGlobalImperativeRedirects()
  useStatsig(statsigUser)
  Experiences.Notifications.Hooks.useNotificationHandler()
  useTrackPage()
  AuthenticationApi.endpoints.getToken.useQuery()

  return <GlobalNavigator />

  // King
  // Setup Global dir
  // -  Navigator
  // -    AuthedNavigator
  // -    NotAuthedNavigator
  // -  Providers

  // isAuthed ? <Global.Navigator.NotAuthedNavigator /> : <Global.Navigator.AuthedNavigator />
}

function RootLayout() {
  const statsigUser = useStatsigUser()
  return (
    <MesaSentry>
      <ReduxProvider store={store}>
        <GestureHandlerRootView style={{ flex: 1, backgroundColor: 'white' }}>
          <TamaguiProvider config={Tamagui} defaultTheme="light">
            <Experiences.Updates.Screens.OTA.Screen>
              <StatsigProvider statsigUser={statsigUser}>
                <ApolloProvider client={apolloClient}>
                  <Experiences.Updates.Screens.VersionWall.Screen>
                    <AlertProvider>
                      <SardineProvider>
                        <PortalProvider>
                          <FontLoadingGate>
                            <RootLayoutNav statsigUser={statsigUser} />
                            <MesaToast />
                            <NewReferralNotification />
                          </FontLoadingGate>
                        </PortalProvider>
                      </SardineProvider>
                    </AlertProvider>
                  </Experiences.Updates.Screens.VersionWall.Screen>
                </ApolloProvider>
              </StatsigProvider>
            </Experiences.Updates.Screens.OTA.Screen>
          </TamaguiProvider>
        </GestureHandlerRootView>
      </ReduxProvider>
    </MesaSentry>
  )
}

export default Sentry.wrap(RootLayout)

export function ErrorBoundary({
  retry,
  error,
  style,
}: ErrorBoundaryProps & Pick<ComponentProps<typeof SafeAreaView>, 'style'>) {
  useEffect(() => {
    sentryCaptureException(error)
    if (!isProduction) console.error(error)
  }, [error])
  const { width, height } = useWindowDimensions()

  return (
    <SafeAreaView
      style={[
        {
          flex: 1,
          backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
          justifyContent: 'center',
          alignItems: 'center',
        },
        style,
      ]}
    >
      <ScrollView
        style={{ width: '100%' }}
        contentContainerStyle={{
          justifyContent: 'center',
          alignItems: 'center',
          paddingHorizontal: 12,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 32 }}>
          <Image
            style={{ width: width * 0.6, height: height * 0.6 }}
            resizeMethod="scale"
            resizeMode="contain"
            source={require('../assets/images/crash.png')}
          />
        </View>

        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            marginTop: 32,
            paddingHorizontal: 12,
          }}
        >
          <Text style={[typography['heading/heading4'], { textAlign: 'center' }]}>
            Oops, something went wrong.
          </Text>
        </View>
        <Text
          style={[
            { marginVertical: 16, marginBottom: 32, lineHeight: 24, textAlign: 'center' },
            typography['label/md-regular'],
          ]}
        >
          We're looking into it, please restart the app and contact us @ support@mesamember.com if
          the issue persists
        </Text>

        <TouchableOpacity
          onPress={() => void (isProduction ? Updates.reloadAsync() : retry())}
          style={{ justifyContent: 'center', alignItems: 'center' }}
        >
          <Text
            style={[
              { color: mesaColorNames['Mesa Primary Scale/500 #00C7B6'] },
              typography['label/lg-regular'],
            ]}
          >
            Reload App
          </Text>
        </TouchableOpacity>

        <Text style={{ marginVertical: 16, marginBottom: 32, lineHeight: 24, textAlign: 'center' }}>
          {`${version}${Updates.updateId ? `@${Updates.updateId}` : ''}`}
        </Text>
      </ScrollView>
    </SafeAreaView>
  )
}
